import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-premium';
import { uniqueId } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
  dateCellValueFormatter,
  getFieldNamesFromColumnConfig,
} from '~/components/SolaForceDataGrid';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { WorktimeReportData } from '~/data/reports';
import { useReportNavigateToView } from '~/scenes/PortalModules/Analytics/common';
import { convertToDateOrUndefined } from '~/utils/helpers/date';
import { getEnumName, getEnumObject } from '~/utils/helpers/enum';

import { useGetCustomFieldValue } from './useGetCustomFieldValue';

type WorktimeReportEntry = {
  empId: number;
  empFirstName: string;
  empLastName: string;
  empNumber: string;
  empPosition: string;
  empUnitName: string;
  empUnitNumber: string;
  country: string;
  legalEntityCode: string;
  legalEntityName: string;
  empPersonnelGroup1: string;
  empPersonnelGroup2: string;
  empPersonnelGroup3: string;
  empPersonnelGroup4: string;
  empPersonnelGroup5: string;
  date: Date | undefined;
  reportingType: string;
  start: string;
  end: string;
  time: string;
};

type PropsType = {
  data: WorktimeReportData[];
};

export const useWorktimeReport = (props: PropsType) => {
  const { data } = props;

  const { goToEmployeeBasicDataView } = useReportNavigateToView();

  const { t } = useTranslation();
  const { getEnums } = useAllEnums();
  const { getCustomFieldValue } = useGetCustomFieldValue();

  const rows = useMemo(() => {
    return data
      .filter((entry) => !!entry.fEmpTimeBlocks?.length)
      .flatMap((entry): WorktimeReportEntry[] => {
        const {
          fEmpId,
          fEmpFirstName,
          fEmpLastName,
          fEmpNumber,
          fEmpPosition,
          fEmpUnitName,
          fEmpUnitNumber,
          fEmpOfficeCountry,
          fEmpLegalEntityCode,
          fEmpLegalEntityName,
          fEmpPersonnelGroups,
          fEmpTimeBlocks,
        } = entry;

        const customFields =
          entry.fEmpTimeBlocks?.flatMap((block) => block.customData || []) ||
          [];

        return fEmpTimeBlocks.map((block) => ({
          uniqueId: uniqueId(),
          empId: fEmpId,
          empFirstName: fEmpFirstName,
          empLastName: fEmpLastName,
          empNumber: fEmpNumber,
          empPosition: fEmpPosition,
          empUnitName: fEmpUnitName,
          empUnitNumber: fEmpUnitNumber,
          country: fEmpOfficeCountry,
          legalEntityCode: fEmpLegalEntityCode,
          legalEntityName: fEmpLegalEntityName,
          empPersonnelGroup1: fEmpPersonnelGroups?.[0] || '',
          empPersonnelGroup2: fEmpPersonnelGroups?.[1] || '',
          empPersonnelGroup3: fEmpPersonnelGroups?.[2] || '',
          empPersonnelGroup4: fEmpPersonnelGroups?.[3] || '',
          empPersonnelGroup5: fEmpPersonnelGroups?.[4] || '',
          date: convertToDateOrUndefined(block.entryDate || ''),
          reportingType: block.reportingType || '',
          start: block.timeFrom || '',
          end: block.timeTo || '',
          time: block.hours || '',
          ...customFields.reduce((acc, customField) => {
            acc[customField.fCustomDataFieldName] =
              customField.fCustomDataFieldValue;
            return acc;
          }, {} as Record<string, string[]>),
        }));
      });
  }, [data]);

  const customDataColumns = data
    .filter((entry) => !!entry.fEmpTimeBlocks?.length)
    .flatMap((entry) => entry.fEmpTimeBlocks || [])
    .flatMap((block) => block.customData || [])
    .reduce((acc, entity) => {
      if (acc[entity.fCustomDataFieldName]) {
        return acc;
      }
      return {
        ...acc,
        [entity.fCustomDataFieldName]: {
          field: entity.fCustomDataFieldName,
          headerName: entity.fCustomDataFieldName,
          valueGetter: () => {
            return getCustomFieldValue(
              entity,
              getEnumObject(
                getEnums(EnumListKey.CUSTOM_ENTITY_TIMESHEET),
                entity.fCustomDataFieldName,
              ),
            );
          },
          minWidth: 100,
        },
      };
    }, {} as { [key: string]: GridColDef<WorktimeReportEntry> });

  const { columnConfigs, columnGroupingModel } = useMemo(() => {
    const countryEnums = getEnums(EnumListKey.COUNTRY);
    const positionEnums = getEnums(EnumListKey.EMPLOYEE_POSITION);

    const personnelGroupEnumsByGroup = Object.fromEntries(
      [
        EnumListKey.PERSONNEL_GROUP_1,
        EnumListKey.PERSONNEL_GROUP_2,
        EnumListKey.PERSONNEL_GROUP_3,
        EnumListKey.PERSONNEL_GROUP_4,
        EnumListKey.PERSONNEL_GROUP_5,
      ].map((key) => {
        return [key, getEnums(key)];
      }),
    );

    const timesheetReportingTypes = [
      ...getEnums(EnumListKey.TIMESHEET_WORK_TYPE),
      ...getEnums(EnumListKey.TIMESHEET_NON_WORK_TYPE),
      ...getEnums(EnumListKey.TIMESHEET_CORRECTION_TYPE),
    ];

    const employeeColumnConfigs: GridColDef<WorktimeReportEntry>[] = [
      {
        field: 'empId',
        headerName: t('laPicture'),
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params) => {
          return (
            <EmployeeCellComponent
              {...params}
              pictureOnly={true}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.empId);
              }}
            />
          );
        },
        minWidth: 60,
      },
      {
        field: 'empFirstName',
        headerName: t('laFirstName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.empId);
              }}
            />
          );
        },
        minWidth: 90,
      },
      {
        field: 'empLastName',
        headerName: t('laLastName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.empId);
              }}
            />
          );
        },
        minWidth: 110,
      },
      {
        field: 'empNumber',
        headerName: t('laEmpId'),
      },
      {
        field: 'empPosition',
        headerName: t('laPosition'),
        minWidth: 150,
        valueGetter: (value) => getEnumName(positionEnums, value),
      },
      {
        field: 'empUnitName',
        headerName: t('laOrgUnit'),
        minWidth: 180,
      },
      {
        field: 'empUnitNumber',
        headerName: t('laUnitNumber'),
      },
      {
        field: 'country',
        headerName: t('laCountry'),
        valueGetter: (value) => getEnumName(countryEnums, value),
      },
      {
        field: 'legalEntityCode',
        headerName: t('laLegalEntityCode'),
      },
      {
        field: 'legalEntityName',
        headerName: t('laLegalEntityName'),
        minWidth: 150,
      },
    ];
    const personnelGroupColumnConfigs: GridColDef<WorktimeReportEntry>[] = [
      {
        field: 'empPersonnelGroup1',
        headerName: t('fPersonnelGroup1'),
        valueGetter: (value) =>
          getEnumName(
            personnelGroupEnumsByGroup[EnumListKey.PERSONNEL_GROUP_1] ?? [],
            value,
          ),
        minWidth: 150,
      },
      {
        field: 'empPersonnelGroup2',
        headerName: t('fPersonnelGroup2'),
        valueGetter: (value) =>
          getEnumName(
            personnelGroupEnumsByGroup[EnumListKey.PERSONNEL_GROUP_2] ?? [],
            value,
          ),
        minWidth: 150,
      },
      {
        field: 'empPersonnelGroup3',
        headerName: t('fPersonnelGroup3'),
        valueGetter: (value) =>
          getEnumName(
            personnelGroupEnumsByGroup[EnumListKey.PERSONNEL_GROUP_3] ?? [],
            value,
          ),
        minWidth: 150,
      },
      {
        field: 'empPersonnelGroup4',
        headerName: t('fPersonnelGroup4'),
        valueGetter: (value) =>
          getEnumName(
            personnelGroupEnumsByGroup[EnumListKey.PERSONNEL_GROUP_4] ?? [],
            value,
          ),
        minWidth: 150,
      },
      {
        field: 'empPersonnelGroup5',
        headerName: t('fPersonnelGroup5'),
        valueGetter: (value) =>
          getEnumName(
            personnelGroupEnumsByGroup[EnumListKey.PERSONNEL_GROUP_5] ?? [],
            value,
          ),
        minWidth: 150,
      },
    ];
    const worktimeColumnConfigs: GridColDef<WorktimeReportEntry>[] = [
      {
        field: 'date',
        headerName: t('laDate'),
        minWidth: 100,
        type: 'date',
        valueFormatter: dateCellValueFormatter,
      },
      {
        field: 'reportingType',
        headerName: t('laReportingType'),
        valueGetter: (value) =>
          getEnumName(timesheetReportingTypes ?? [], value),
        minWidth: 150,
      },
      {
        field: 'start',
        headerName: t('laStart'),
        minWidth: 100,
      },
      {
        field: 'end',
        headerName: t('laEnd'),
        minWidth: 100,
      },
      {
        field: 'time',
        headerName: t('laTime'),
        minWidth: 100,
      },
      ...Object.values(customDataColumns),
    ];

    const columnConfigs: GridColDef<WorktimeReportEntry>[] = [
      ...employeeColumnConfigs,
      ...worktimeColumnConfigs,
      ...personnelGroupColumnConfigs,
    ];

    const columnGroupingModel: GridColumnGroupingModel = [
      {
        groupId: 'employee',
        headerName: t('laEmployee'),
        children: getFieldNamesFromColumnConfig(employeeColumnConfigs),
      },
      {
        groupId: 'worktime',
        headerName: t('laWorktime'),
        children: getFieldNamesFromColumnConfig(worktimeColumnConfigs),
      },
      {
        groupId: 'personnelGroups',
        headerName: t('laPersonnelGroups'),
        children: getFieldNamesFromColumnConfig(personnelGroupColumnConfigs),
      },
    ];

    return {
      columnConfigs,
      columnGroupingModel,
    };
  }, [t, goToEmployeeBasicDataView, getEnums, customDataColumns]);

  return {
    table: {
      columnConfigs,
      rows,
      columnGroupingModel,
    },
  };
};
